/** The two full-screen materials of the frame (05 §8): the low-resolution post pass and the integer upscale. */

import {
  Color,
  type Data3DTexture,
  type DepthTexture,
  GLSL3,
  RawShaderMaterial,
  type Texture,
  Vector2,
} from 'three';
import { BORDER, TARGET_H, TARGET_W, VIEW_H, VIEW_W } from '../camera/PixelCamera';
import { sharedUniforms } from '../materials/toon';
import { LUT_SIZE } from '../palette';
import { NVG_STEPS, phosphorHex } from './options';
import fullscreenVert from './shaders/fullscreen.vert.glsl?raw';
import postFrag from './shaders/post.frag.glsl?raw';
import upscaleFrag from './shaders/upscale.frag.glsl?raw';

export interface PostInputs {
  color: Texture;
  normalId: Texture;
  depth: DepthTexture;
  lut: Data3DTexture;
  /** Any valid texture: only sampled while the flash cap is on, when the pipeline swaps the real one in. */
  previous: Texture;
  /** Outline colour, linear. */
  ink: Color;
}

/** Outlines, palette lock, haze, grade, and the post-lock options - every option uniform starts neutral. */
export function createPostMaterial(inputs: PostInputs): RawShaderMaterial {
  const ink = inputs.ink.clone().convertLinearToSRGB();
  const phosphor = new Color(phosphorHex()).convertLinearToSRGB();
  return new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: postFrag,
    depthTest: false,
    depthWrite: false,
    uniforms: {
      tColor: { value: inputs.color },
      tNormalId: { value: inputs.normalId },
      tDepth: { value: inputs.depth },
      tLut: { value: inputs.lut },
      uTexel: { value: new Vector2(1 / TARGET_W, 1 / TARGET_H) },
      uCamTexel: sharedUniforms.uCamTexel,
      uNear: { value: 1 },
      uFar: { value: 100 },
      uInk: { value: [ink.r, ink.g, ink.b] },
      uHaze: { value: new Color() },
      uHazeAmount: { value: 0 },
      uDepthMid: { value: 0 },
      uDepthSpan: { value: 1 },
      uPaletteLock: { value: 0.85 },
      uDitherAmp: { value: 0.07 },
      uLutSize: { value: LUT_SIZE },
      uTint: { value: [1, 1, 1] },
      uTintAmount: { value: 0 },
      uValueScale: { value: 1 },
      uOutlines: { value: 1 },
      uPlayerRim: { value: [ink.r, ink.g, ink.b] },
      uPlayerRimAfterGrade: { value: 0 },
      uNvg: { value: 0 },
      uPhosphor: { value: [phosphor.r, phosphor.g, phosphor.b] },
      uNvgSteps: { value: NVG_STEPS },
      tPrev: { value: inputs.previous },
      uPrevShift: { value: new Vector2() },
      uFlashCap: { value: 0 },
    },
  });
}

/** Nearest-neighbour blow-up with the sub-texel scroll, the vignette and the optional scanlines. */
export function createUpscaleMaterial(image: Texture): RawShaderMaterial {
  return new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: upscaleFrag,
    depthTest: false,
    depthWrite: false,
    uniforms: {
      tImage: { value: image },
      uTargetSize: { value: new Vector2(TARGET_W, TARGET_H) },
      uViewSize: { value: new Vector2(VIEW_W, VIEW_H) },
      uBorder: { value: BORDER },
      uSubTexel: { value: new Vector2() },
      uVignette: { value: 0.18 },
      uScale: { value: 1 },
      uScanline: { value: 1 },
      uVignetteBoost: { value: 0 },
    },
  });
}
